// src/pages/admin/AdminVouchers.js — Admin : gestion des coupons / vouchers
// Maxula
import React, { useEffect, useState, useCallback } from 'react';
import { FiTag, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { voucherAPI } from '../../services/api';

const STATUS = {
  active: { color: 'bg-green-100 text-green-800', text: 'Actif' },
  used: { color: 'bg-gray-200 text-gray-700', text: 'Utilisé' },
  expired: { color: 'bg-red-100 text-red-800', text: 'Expiré' },
  cancelled: { color: 'bg-yellow-100 text-yellow-800', text: 'Annulé' }
};
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};
const clientName = (u) => (u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '—' : '—');

const Badge = ({ status }) => {
  const c = STATUS[status] || { color: 'bg-gray-100 text-gray-700', text: status || '—' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.color}`}>{c.text}</span>;
};

const AdminVouchers = () => {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await voucherAPI.adminList({ status: status || undefined, limit: 200 });
      setRows(Array.isArray(data?.data) ? data.data : []);
      setStats({ total: data?.pagination?.total ?? (data?.data?.length || 0) });
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((v) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (
      (v.code || '').toLowerCase().includes(s) ||
      (v.deal?.title || '').toLowerCase().includes(s) ||
      (v.merchant?.businessName || '').toLowerCase().includes(s) ||
      clientName(v.user).toLowerCase().includes(s)
    );
  });

  return (
    <div className="px-4 py-6">
      <div className="flex items-center gap-2 mb-1">
        <FiTag className="text-primary-600" size={22} />
        <h1 className="text-2xl font-bold">Gestion des vouchers</h1>
      </div>
      <p className="text-gray-500 text-sm mb-6">Tous les coupons générés après paiement ({stats.total} au total).</p>

      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher (code, deal, marchand, client)…"
              className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
            />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded-lg px-2 py-2 text-sm">
            <option value="">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="used">Utilisés</option>
            <option value="expired">Expirés</option>
            <option value="cancelled">Annulés</option>
          </select>
          <button onClick={load} className="p-2 rounded-lg border hover:bg-gray-50" title="Rafraîchir"><FiRefreshCw size={16} /></button>
        </div>

        {loading ? (
          <div className="text-center text-gray-400 py-10">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-gray-400 py-10">Aucun voucher.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-3">Code</th>
                  <th className="py-2 pr-3">Deal</th>
                  <th className="py-2 pr-3">Marchand</th>
                  <th className="py-2 pr-3">Client</th>
                  <th className="py-2 pr-3">Statut</th>
                  <th className="py-2 pr-3">Créé</th>
                  <th className="py-2 pr-3">Expire</th>
                  <th className="py-2 pr-3">Utilisé</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => (
                  <tr key={v.id || v.code} className="border-b last:border-0">
                    <td className="py-2 pr-3 font-mono font-medium">{v.code}</td>
                    <td className="py-2 pr-3">{v.deal?.title || '—'}</td>
                    <td className="py-2 pr-3">{v.merchant?.businessName || '—'}</td>
                    <td className="py-2 pr-3">{clientName(v.user)}</td>
                    <td className="py-2 pr-3"><Badge status={v.status} /></td>
                    <td className="py-2 pr-3">{fmtDate(v.createdAt)}</td>
                    <td className="py-2 pr-3">{fmtDate(v.expiresAt)}</td>
                    <td className="py-2 pr-3">{v.usedAt ? fmtDate(v.usedAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminVouchers;
